import React, { useState, useEffect } from "react";
import {
  Show,
  SimpleShowLayout,
  TextField,
  TopToolbar,
  useResourceContext,
  Labeled,
  useTranslate,
  useRecordContext
} from "react-admin";
import alasql from "alasql";
import { generateField } from "./factory";
import WorkFlowButton from "./WorkFlowButton";
import { useMediaQuery, Grid, Box, Typography } from "@mui/material";

/**
 * Renders a list of fields in a grid layout (with optional separators and labels).
 * 
 * @param {Object} props - The original props passed down (includes 'id', etc.)
 * @param {Array} filteredFields - An array of field configurations to render
 * @param {Function} translate - The translation function from react-admin
 * @param {String} resource - The current resource name
 */
export const renderFields = (props, filteredFields, translate, resource) => {
  // Suppose 'labeledFields' and 'generateInputField' come from your internal logic/factory
  // This snippet references them as in your original code.
  // Make sure they are accessible here or imported from your factory.
  const labeledFields = ["text_field", "date_field", "select_field","reference_text_new_disabled"]; // example

  const generateInputField = (fieldConfig, key, recordId) => {
    // This function should mimic whatever you're doing with generateField or a similar factory
    // In your snippet, you appear to use something called generateField or generateInputField.
    // Provide your own implementation here. 
    // For demonstration, we just call generateField for now:
    return generateField(fieldConfig, key, {}, "", recordId);
  };

  return (
    <Grid container item columnSpacing={2}>
      {filteredFields.map((f, innerIndex) => (
        <React.Fragment key={innerIndex}>
          {f.is_require_separator && <Box width="100%" />}
          {f.is_require_label && (
            <>
              <Typography variant="h6" gutterBottom>
                {f.label_text}
              </Typography>
              <Box width="100%" />
            </>
          )}
          <Grid {...props} item size={{ xs: f.grid_xs || 4, sm: f.grid_sm || 4 }}>
            <Box
              sx={{
                borderRadius: "8px",
                padding: "10px",
                marginBottom: "8px",
                backgroundColor: "#fff",
                boxShadow: "0px 2px 4px rgba(0, 0, 0, 0.05)",
              }}
            >
              {labeledFields.includes(f.field_widget_type) ? (
                <Labeled
                  sx={{
                    "& .RaLabeled-label": {
                      fontWeight: "bold",
                      fontSize: "14px",
                      color: "#000",
                      paddingBottom: "5px",
                      display: "block",
                    },
                  }}
                >
                  {generateInputField(f, innerIndex, props.id)}
                </Labeled>
              ) : (
                generateInputField(f, innerIndex, props.id)
              )}
            </Box>
          </Grid>
        </React.Fragment>
      ))}
    </Grid>
  );
};

/**
 * Renders any workflow actions for the Show view.
 */
export const ShowActions = ({ basePath, data, resource, columns }) => {
  // By default, no actions
  let Hooks = [];
  let workflow = "none";
  let actions = [];

  if (typeof data !== "undefined") {
    const role = localStorage.getItem("role");

    // Get workflow key
    let stc = alasql(
      `SELECT DISTINCT StateColumn as col FROM workflows 
         WHERE Module ='${resource}' 
           AND role_${role}='control'`
    ).map(({ col }) => col);

    if (stc && stc[0]) {
      if (isNaN(`${data[stc[0]]}`)) {
        actions = alasql(
          `SELECT * FROM workflows 
             WHERE Module ='${resource}' 
               AND role_${role}='control' 
               AND StateColumn='${stc[0]}' 
               AND StateValue='${data[stc[0]]}'`
        ).map((col) => col);
      } else {
        actions = alasql(
          `SELECT * FROM workflows 
             WHERE Module ='${resource}' 
               AND role_${role}='control' 
               AND StateColumn='${stc[0]}' 
               AND StateValue=${data[stc[0]]}`
        ).map((col) => col);
      }

      if (
        typeof actions[0] !== "undefined" &&
        typeof actions[0].Actions !== "undefined"
      ) {
        let actData = actions[0].Actions;
        if (actData !== "[]") {
          Hooks = alasql(
            `SELECT * FROM workflows 
               WHERE Module ='${resource}' 
                 AND role_${role}='control' 
                 AND WorkFlowName= ANY (ARRAY${actData})`
          ).map((col) => col);
          workflow = actions[0].WorkFlowName;
        }
      }
    }
  }

  return (
    <TopToolbar>
      {/* Map over any discovered workflow hooks and render them */}
      {Hooks.map((f, index) => (
        <WorkFlowButton actData={f} key={index} basePath={basePath} record={data} />
      ))}
    </TopToolbar>
  );
};

/**
 * The main Show component refactored to a functional component,
 * preserving the original logic/naming as closely as possible.
 */
const ResourceShow = (props) => {
  const { id } = props;
  const resource = useResourceContext();

  const translate = useTranslate();
  const [resourcesFields, setResourcesFields] = useState([]);

  const [loading, setLoading] = useState(true);
  const [filteredFields, setFilteredFields] = useState([]);
  const [resourcesTabs, setResourcesTabs] = useState([]);
  const [tabs, setTabs] = useState([]);
  const [headerFields, setHeaderFields] = useState([]);
  const record = useRecordContext(); // 🔹 Now directly using useRecordContext()


  
    useEffect(() => {
      if (!record) return; // 🔹 Prevents running if record is not available yet
  

    /**
     * Mimics componentDidMount logic:
     * loads resource-specific data from alasql
     * and sets up state for rendering.
     */
    const init = async () => {
      const role = localStorage.getItem("role");

      // Tabs
      const tabsData = alasql(
        `select distinct write_group as tab 
         from resources 
         where resource_name ='${resource}' 
           and is_show_field=true 
           and ( role_${role}='read' OR role_${role}='write')`
      ).map(({ tab }) => tab);

 
      let resourcesFieldsData = alasql(
        `SELECT DISTINCT write_group AS tab FROM resources WHERE resource_name ='${resource}'  `
      ).map(({ tab }) =>
        alasql(`SELECT watched_column,grid_class, grid_xs, grid_sm, is_require_separator, is_require_label, label_text, source_name,
          CASE
            WHEN role_${role} = 'read' THEN field_widget_type
            ELSE edit_input_widget_type
          END AS input_widget_type,
          field_meta, annotation, css_class, edit_css_class, full_width, is_required,
          { 
            "workflow_conditional": workflow_conditional, 
            "workflow_column": workflow_column, 
            "workflow_conditional_show": workflow_conditional_show, 
            "workflow_conditional_readonly": workflow_conditional_hide,
            "workflow_conditional_readonly_type":field_widget_type
          } AS workflow_info
          FROM resources WHERE write_group = "${tab}" AND resource_name = '${resource}'
          AND ((role_${role} = 'write' AND is_edit_field = true) OR (role_${role} = 'read')) `)
      );

      // Header fields
      const headerFieldsData = alasql(
        `select watched_column, source_name, field_widget_type, 
                field_meta, annotation, grid_sm, grid_xs  
         from resources 
         where resource_name ='${resource}'  
           and is_show_field = true
           and ( role_${role}='write' ) 
           and annotation ="header"`
      );

      // Custom tab layout info
      const resourcesTabsData = alasql(`
        SELECT DISTINCT r.write_group AS tab,
                        COALESCE(ct.width_class, 12) AS width_class,
                        COALESCE(ct.new_line_after, false) AS new_line_after
        FROM resources AS r
        LEFT JOIN custom_tabs AS ct
               ON r.resource_name = ct.resource_name 
              AND r.write_group = ct.tab_id
        WHERE r.resource_name = '${resource}'
      `).map(({ tab, width_class, new_line_after }) => ({
        tab,
        width_class,
        new_line_after
      }));

      const filteredFieldsData = resourcesFieldsData.map(
        (modelFields) =>
          modelFields &&
          modelFields.filter((f) => {
            if (
              f.workflow_info &&
              f.workflow_info.workflow_conditional === true &&
              record &&
              record[f.workflow_info.workflow_column]
            ) {
              const workflowColumn = f.workflow_info.workflow_column;
              const workflowConditionalShow = f.workflow_info.workflow_conditional_show.split(',').map(Number);
              const workflowConditionalReadonly = f.workflow_info.workflow_conditional_readonly.split(',').map(Number);
    
              if (
                record[workflowColumn] &&
                workflowConditionalShow.includes(record[workflowColumn])
              ) {
                if (
                  record[workflowColumn] &&
                  workflowConditionalReadonly.includes(record[workflowColumn])
                ) {
                  f.field_widget_type =
                    f.workflow_info.workflow_conditional_readonly_type;
                }
                return true;
              }
              return false;
            }
            return true;
          })
      );

    
      setFilteredFields(filteredFieldsData);
      // Update local state
      setLoading(false);
      setResourcesFields(resourcesFieldsData);
      setFilteredFields(filteredFieldsData);
      setResourcesTabs(resourcesTabsData);
      setTabs(tabsData);
      setHeaderFields(headerFieldsData);
    };

    init();
  }, [resource, record]);

  /**
   * Renders a single field (mimicking the original class method).
   * 
   * @param {Object} f - The field configuration
   * @param {Number} index - Index for the key
   */
  const renderField = (f, index) => {
    return generateField(f, index, record, "", id);
  };

  if (loading) return "loading ...";

  return (
    <>
      {headerFields.length > 0 && (
        <Show {...props} actions={<></>}>
          <SimpleShowLayout>
            {headerFields.map((f, index) => (
              <TextField
                source={f.source_name}
                className="show-field-compressed"
                key={index}
              />
            ))}
          </SimpleShowLayout>
          <hr />

        </Show>
      )}
      <Show actions={<ShowActions />} {...props}>
        <SimpleShowLayout>
          <Grid container width={{ xs: "100%" }} spacing={2}>
            {resourcesTabs.map((t, index) => {
              if (filteredFields[index] && filteredFields[index].length > 0) {
                return (
                  <Grid {...props} item size={{ xs: t.width_class, sm: t.width_class }} key={index}>
                    <Typography variant="h6" gutterBottom>
                      {t.tab}
                    </Typography>
                    {renderFields(props, filteredFields[index], translate, resource)}
                  </Grid>
                );
              }
              return null;
            })}
          </Grid>
        </SimpleShowLayout>
      </Show>
    </>
  );
};

/**
 * A wrapper that uses React-Admin's useResourceContext to provide the `resource` prop
 * to ResourceShow, preserving the original approach.
 */
  const UiResourceShow = (props) => {
    return (
      <Show {...props}>
        <ResourceShow />
      </Show>
    );
  };
  
  export default UiResourceShow;

